import { Component, inject, OnInit } from "@angular/core";
import { CustomerService, UserService } from "../../_services";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {
  faArrowLeft,
  faCheck,
  faEdit,
  faIndustry,
  faKey,
  faPhone,
  faTrash,
  faUpload,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { faProductHunt } from "@fortawesome/free-brands-svg-icons";
import { AddressOutput, EditAddressComponent } from "../_shared/edit-address/edit-address.component";
import { AG_GRID_LOCALE_DE } from "../../_helpers/aggrid.locale.de";
import { ColDef, GridReadyEvent } from "ag-grid-community";
import { GridApi, RowClassRules, SideBarDef } from "ag-grid-enterprise";
import {
  APCustomer,
  APCustomerProject,
  APCustomerUser,
  PermittedCustomerParams,
} from "../../_models/customer.interface";
import { APStore } from "../../_models/store.interface";
import { MatOption } from "@angular/material/core";
import { MatFormField, MatLabel, MatSelect } from "@angular/material/select";
import { EditInputComponent } from "../_shared/edit-input/edit-input.component";

import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { NgxMapboxGLModule } from "ngx-mapbox-gl";
import { MatTabChangeEvent } from "@angular/material/tabs";
import { AgGridAngular } from "ag-grid-angular";
import { FormsModule } from "@angular/forms";
import { AgGridThemeService } from "../../_services/ag-grid-theme.service";
import { RecordNavigationComponent } from "../_shared/record-navigation/record-navigation.component";
import { Article } from "../../_models";

export type CustomerShowTab = "stores" | "projects" | "articles";
@Component({
  selector: "hawk-customer-show",
  templateUrl: "./customer-show.component.html",
  standalone: true,
  imports: [
    MatOption,
    MatSelect,
    MatLabel,
    MatFormField,
    RecordNavigationComponent,
    EditInputComponent,
    RouterLink,
    FaIconComponent,
    NgxMapboxGLModule,
    AgGridAngular,
    EditAddressComponent,
    FormsModule,
  ],
  styleUrls: ["./customer-show.component.scss"],
  host: {
    class: "app-page",
  },
})
export class CustomerShowComponent implements OnInit {
  public hawkTheme = inject(AgGridThemeService).getTheme();

  protected readonly faArrowLeft = faArrowLeft;
  protected readonly faCheck = faCheck;
  protected readonly faPhone = faPhone;
  protected readonly faUser = faUser;
  protected readonly faProductHunt = faProductHunt;
  protected readonly faEdit = faEdit;
  protected readonly faTrash = faTrash;
  protected readonly faIndustry = faIndustry;
  protected readonly faKey = faKey;
  protected tab: CustomerShowTab = "stores";

  customer: APCustomer = null;
  stores: APStore[] = [];
  projects: APCustomerProject[] = [];
  customerIsDeletable = false;

  public editLetter = false;
  public editName = false;
  public editURL = false;
  public editXMLID = false;
  public editAdditionalInformation = false;
  public editDeliveryNoteConfirmationMail = false;
  public editAddress = false;

  public showModal?: "address" | "return_address" | "user_project_access" = null;
  public userProjectAccessUser?: APCustomerUser = null;
  public selectedDefaultUserProjectId: number = null;

  public articles: Article[] = [];

  constructor(
    private customerService: CustomerService,
    private userService: UserService,
    public route: ActivatedRoute,
    public router: Router,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get("id");
    this.updateCustomer(id);
    this.updateStores(id);
    this.updateArticles(id);
    this.updateProjects(id);
    this.updateDeletable(id);
  }

  updateCustomer(id: string) {
    this.customerService.loadAPCustomer(id).subscribe(
      (data) => {
        this.customer = data;
      },
      (error) => {
        console.log(error);
      },
    );
  }

  updateStores(id: string) {
    this.customerService.loadAPCustomerStores(id).subscribe(
      (data) => {
        this.stores = data;
      },
      (error) => {
        console.log(error);
      },
    );
  }

  updateArticles(id: string) {
    this.customerService.loadCustomerArticles(id).subscribe(
      (data) => {
        this.articles = data;
      },
      (error) => {
        console.log(error);
      },
    );
  }

  updateProjects(id: string) {
    this.customerService.loadAPCustomerProjects(id).subscribe(
      (data) => {
        this.projects = data;
      },
      (error) => {
        console.log(error);
      },
    );
  }

  updateDeletable(id: string): void {
    this.customerService.customerIsDeletable(id).subscribe(
      (data) => {
        this.customerIsDeletable = data.deletable;
      },
      (err) => {
        console.log(err);
      },
    );
  }

  deleteCustomer(): void {
    this.customerService.deleteCustomer(this.route.snapshot.paramMap.get("id")).subscribe(
      (_) => {
        this.router.navigate(["/kunde"]);
      },
      (error) => {
        console.log(error);
      },
    );
  }

  saveName(name: string): void {
    this.saveCustomerValues({ name }).then((success) => {
      if (success) this.editName = false;
    });
  }

  saveLetter(letter: string): void {
    this.saveCustomerValues({ letter }).then((success) => {
      if (success) this.editLetter = false;
    });
  }

  saveURL(url: string): void {
    this.saveCustomerValues({ url }).then((success) => {
      if (success) this.editURL = false;
    });
  }

  saveXMLID(xml_id: string): void {
    this.saveCustomerValues({ xml_id }).then((success) => {
      if (success) this.editXMLID = false;
    });
  }

  saveAddress(address: AddressOutput) {
    this.saveCustomerValues(address).then((success) => {
      if (success) this.showModal = null;
    });
  }

  get returnAddressMatchesCustomer(): boolean {
    if (!this.customer) return false;
    const c = this.customer;
    return (c.return_address_company || "") === (c.company || "") &&
      (c.return_address_street || "") === (c.street || "") &&
      (c.return_address_zip || "") === (c.zip || "") &&
      (c.return_address_city || "") === (c.city || "") &&
      (c.return_address_country || "") === (c.country || "");
  }

  setReturnAddressFromCustomer(): void {
    if (!confirm("Rücksendeadresse mit der Kundenadresse überschreiben?")) return;
    this.saveCustomerValues({
      return_address_company: this.customer.company || "",
      return_address_firstname: "",
      return_address_lastname: "",
      return_address_street: this.customer.street || "",
      return_address_zip: this.customer.zip || "",
      return_address_city: this.customer.city || "",
      return_address_country: this.customer.country || "",
    });
  }

  saveReturnAddressFromModal(address: AddressOutput): void {
    const params: PermittedCustomerParams = {
      return_address_company: address.address_company,
      return_address_firstname: address.address_firstname,
      return_address_lastname: address.address_lastname,
      return_address_street: address.address_street,
      return_address_zip: address.address_zip,
      return_address_city: address.address_city,
      return_address_country: address.address_country,
    };
    this.saveCustomerValues(params).then((success) => {
      if (success) this.showModal = null;
    });
  }

  saveAdditionalInformation(additional_information: string): void {
    this.saveCustomerValues({ additional_information }).then((success) => {
      if (success) this.editAdditionalInformation = false;
    });
  }

  saveDeliveryNoteConfirmationMail(delivery_note_confirmation_mail: string): void {
    this.saveCustomerValues({ delivery_note_confirmation_mail }).then((success) => {
      if (success) this.editDeliveryNoteConfirmationMail = false;
    });
  }

  saveUserProjectAccessUser() {
    const promise = new Promise<boolean>((resolve) => {
      this.userService
        .updateUser(this.userProjectAccessUser.id, { project_id: this.selectedDefaultUserProjectId })
        .subscribe(
          (_) => {
            resolve(true);
          },
          (error) => {
            console.log(error);
            resolve(false);
          },
        );
    });

    promise.then((success) => {
      if (success) {
        this.updateCustomer(this.route.snapshot.paramMap.get("id"));
        this.showModal = null;
      }
    });
  }

  async saveCustomerValues(customer_values: PermittedCustomerParams): Promise<boolean> {
    return await new Promise<boolean>((resolve) => {
      this.customerService.updateCustomer(this.customer.id, customer_values).subscribe(
        (data) => {
          this.customer = data;
          resolve(true);
        },
        (error) => {
          console.log(error);
          resolve(false);
        },
      );
    });
  }

  public storeColDefs: ColDef[] = [
    {
      field: "nr",
      headerName: "Nr.",
      flex: 1,
      minWidth: 90,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
      minWidth: 120,
      cellRenderer: this.storeNameRenderer,
      cellClass: "hover_link_cell",
    },
    {
      field: "address",
      headerName: "Adresse",
      flex: 2.5,
      minWidth: 180,
    },
    {
      field: "phone1",
      headerName: "Telefon",
      flex: 1,
      minWidth: 100,
      cellRenderer: (params: any) =>
        params.value ? `<a class="bm-link" href="tel:${params.value}">${params.value}</a>` : "",
    },
    {
      field: "email",
      headerName: "E-Mail",
      flex: 1.5,
      minWidth: 120,
      cellRenderer: (params: any) =>
        params.value ? `<a class="bm-link" href="mailto:${params.value}">${params.value}</a>` : "",
    },
    {
      field: "register",
      headerName: "Kassen",
      flex: 0.8,
      minWidth: 80,
    },
    {
      field: "ip",
      headerName: "IP",
      flex: 1,
      minWidth: 90,
    },
    {
      field: "project_count",
      headerName: "Projekte",
      flex: 0.8,
      minWidth: 80,
    },
    {
      field: "comment",
      headerName: "Kommentar",
      flex: 1.5,
      minWidth: 100,
    },
  ];

  public projectColDefs: ColDef[] = [
    {
      field: "active",
      headerName: "Aktiv",
      flex: 0.7,
      minWidth: 80,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 2,
      minWidth: 150,
      cellRenderer: this.projectNameRenderer,
      cellClass: "hover_link_cell",
    },
    {
      field: "description",
      headerName: "Beschreibung",
      flex: 2.5,
      minWidth: 180,
    },
    {
      field: "ticket_count",
      headerName: "Tickets",
      flex: 1,
      minWidth: 90,
    },
    {
      field: "store_count",
      headerName: "Stores",
      flex: 1,
      minWidth: 90,
    },
  ];

  public articleColDefs: ColDef[] = [
    {
      field: "description",
      headerName: "Beschreibung",
      flex: 1,
    },
    {
      field: "location_translated",
      headerName: "Ort",
      flex: 1,
    },
    {
      field: "warehouse_name",
      headerName: "Lager",
      flex: 1,
      cellRenderer: this.warehouseRenderer,
    },
    {
      field: "supplier_name",
      headerName: "Hersteller",
      flex: 1,
    },
  ];

  private storeGridApi!: GridApi;
  private projectGridApi!: GridApi;
  private articleGridApi!: GridApi;
  public rowClassRules: RowClassRules = {};

  public sideBarOptions: SideBarDef = {
    hiddenByDefault: false,
    toolPanels: ["columns", "filters"],
  };

  public defaultColDef: ColDef = {
    filter: true,
  };

  public rowSelection: "single" | "multiple" = "single";

  protected readonly alert = alert;
  protected readonly locale = AG_GRID_LOCALE_DE;

  storeNameRenderer(params: any): string {
    const id = params.data.id;
    return `<a class="bm-link" href="/a/filiale/${id}" rel="noopener">${params.value}</a>`;
  }

  projectNameRenderer(params: any): string {
    const id = params.data.id;
    return `<a class="bm-link" href="/a/projekt/${id}" rel="noopener">${params.value}</a>`;
  }

  warehouseRenderer(params: any): string {
    const id = params.data.warehouse_id;
    return id ? `<a class="bm-link" href="/a/lager/${id}" rel="noopener">${params.value}</a>` : "";
  }

  onStoreGridReady($event: GridReadyEvent<any>) {
    this.storeGridApi = $event.api;
  }

  onProjectGridReady($event: GridReadyEvent<any>) {
    this.projectGridApi = $event.api;
  }

  onArticleGridReady($event: GridReadyEvent<any>) {
    this.articleGridApi = $event.api;
  }

  public storeQuickSearchChanged(e: Event) {
    const newValue = (e.target as HTMLInputElement).value;
    this.storeGridApi.setGridOption("quickFilterText", newValue);
  }

  public projectQuickSearchChanged(e: Event) {
    const newValue = (e.target as HTMLInputElement).value;
    this.projectGridApi.setGridOption("quickFilterText", newValue);
  }

  public articleQuickSearchChanged(e: Event) {
    const newValue = (e.target as HTMLInputElement).value;
    this.articleGridApi.setGridOption("quickFilterText", newValue);
  }

  protected readonly faUpload = faUpload;

  navigateToStoreImport() {
    this.router.navigate([`/kunde/${this.route.snapshot.paramMap.get("id")}/filialen_import`]);
  }

  updateTableSizes($event: MatTabChangeEvent) {
    if ($event.index == 1) {
      this.projectGridApi.sizeColumnsToFit();
    } else {
      this.storeGridApi.sizeColumnsToFit();
    }
  }
}
